import { useState, useEffect, useContext, useCallback, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import TevelCyberLogo from "../../../assets/img/TevelCyberFinalBlueLogo.png";
import { useNavigate } from "react-router-dom";
import UserService from "../../../services/UserService";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import CyberBay from "../../../assets/img/CyberBayFinalBlueLogo.png";
import LoginImage from "../../../assets/img/LoginIllustraion.png";

const QrCode = () => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [codeIsValid, setCodeIsValid] = useState(true);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const loadControllerRef = useRef(null);
  const submitControllerRef = useRef(null);


  const validateSetupCompleted = useCallback(() => {
    if (
      authContext.mfaSetupEnabled === false ||
      authContext.mfaSetupCompleted
    ) {
      navigate("/");
    }
  }, [authContext, navigate]);

const loadQrCode = useCallback(async () => {
  if (loadControllerRef.current) {
    loadControllerRef.current.abort();
  }
  const controller = new AbortController();
  loadControllerRef.current = controller;

  setLoading(true);

  try {
    const data = await UserService.GetQrCode(
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      setQrCode(data.data.qrCodeSetupImageUrl);
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (error) {
    if (error.name === "AbortError") return;
    AlertService.error("Failed to load QR code");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
}, [authContext]);

useEffect(() => {
  validateSetupCompleted();
  loadQrCode();
  return () => loadControllerRef.current?.abort();
}, [validateSetupCompleted, loadQrCode]);

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        if (index > 0) document.getElementById(`otp-${index - 1}`).focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pasteData)) return;
    const newOtp = Array(6).fill("");
    [...pasteData].forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);
    const nextIndex = pasteData.length >= 6 ? 5 : pasteData.length;
    document.getElementById(`otp-${nextIndex}`).focus();
  };

 const handleSubmit = async (event) => {
  event.preventDefault();
  const finalOtp = otp.join("");

  if (finalOtp.length < 6) {
    setCodeIsValid(false);
    return;
  }

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const result = await UserService.VerifyQrCode(
      finalOtp,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      if (authContext.isAnswered === false) {
        navigate("/questions");
      } else {
        navigate("/logout");
      }
    } else {
      AlertService.error(result.errorMessage);
    }
  } catch (error) {
    if (error.name === "AbortError") return;
    AlertService.error("Verification failed");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

useEffect(() => {
  return () => {
    loadControllerRef.current?.abort();
    submitControllerRef.current?.abort();
  };
}, []);

  return (
    <div className="container-fluid min-vh-100 d-flex flex-column flex-xl-row p-0">
      <div className="col-xl-6 d-flex flex-column justify-content-center align-items-center p-3 p-lg-5 min-vh-100  ">
        <div className="d-none d-xl-block mt-2">
          <img
            src={TevelCyberLogo}
            alt="Company Logo"
            style={{ maxWidth: "217px", marginRight: "220px" }}
            className="img-fluid"
          />
        </div>
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100">
          <div className="d-flex d-xl-none justify-content-between align-items-center gap-5 mt-3 mb-4">
            <img
              src={TevelCyberLogo}
              alt="Tevel Cyber Logo"
              className="img-fluid"
              style={{ width: "140px" }}
            />
            <img
              src={CyberBay}
              alt="Cyber Bay Logo"
              className="img-fluid"
              style={{ width: "120px" }}
            />
          </div>
          <div className="w-100 mb-xl-4" style={{ maxWidth: "440px" }}>
            <h4 className="mb-3 text-center fw-bold">
              Two Factor Verification Setup
            </h4>
            <p className="mb-2 text-center">
              Please scan the QR code displayed below using any authenticator
              app.
            </p>
            {qrCode && (
              <div className="text-center mb-2">
                <img
                  src={qrCode}
                  alt="QR Code"
                  className="img-fluid"
                  style={{ maxWidth: "200px", height: "200px" }}
                />
              </div>
            )}
            <p className="mb-3 text-center">
              Once your email is added to the list, verify the One-Time Password
              code generated by your authenticator app in the textbox below.
            </p>

            <form onSubmit={handleSubmit}>
              <div
                className="d-flex justify-content-between mb-4 gap-2"
                onPaste={handlePaste}
              >
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="text"
                    maxLength="1"
                    className="form-control text-center mfa-input"
                    value={digit}
                    onChange={(e) => handleOtpChange(e, i)}
                    onKeyDown={(e) => handleOtpKeyDown(e, i)}
                    autoComplete="off"
                    autoFocus={i === 0} 
                    disabled={loading}
                  />
                ))}
              </div>
              {!codeIsValid && (
                <div className="text-danger mb-2">
                  Please enter a valid 6-digit code
                </div>
              )}
              <button
                type="submit"
                className="btn w-100 btn-primary custom-input "
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                {loading ? "Processing..." : "Verify & Complete Setup"}
              </button>
            </form>
          </div>
        </div>
        <footer className="mt-auto text-center w-100">
          <p className="text-muted small mb-0">
            © 2026 Tevel Cyber Corps Pvt. Ltd. All rights reserved - Version 2.2.0
          </p>
        </footer>
      </div>
      <div className="col-xl-6 d-none d-xl-flex flex-column login-bg  min-vh-100 align-items-center justify-content-center  ">
        <div className="p-5 mt-2">
          <img
            src={CyberBay}
            alt="CYBER BAY"
            className="img-fluid mb-4"
            width="130px"
          />
        </div>
        <div className='"d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100 p-xl-5 '>
          <img
            src={LoginImage}
            alt="Decorative"
            className="img-fluid loginImg"
            style={{
              maxWidth: "690px",
              maxHeight: "484px",
              marginLeft: "130px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default QrCode;
